/**
 * Het kleine diagram op een productkaart in het scherm Prijsgrafiek.
 *
 * Twee lijnen zonder assen en zonder labels: onze prijs en de laagste marktprijs. Er staat bewust
 * geen schaal bij — de vraag hier is niet "hoeveel" maar "loopt er iets uit elkaar". De bedragen
 * staan eronder in tekst.
 *
 * De schaal is per kaart en niet over alle kaarten gelijk. Producten van € 30 en € 300 op één schaal
 * zou de goedkope kaartjes tot een vlakke streep platdrukken, en juist die beweging is waar je op
 * zoek bent.
 */

const BREEDTE = 300;
const HOOGTE = 74;
const RAND = 7;

export function MiniGrafiek({
  eigen,
  laagste,
}: {
  eigen: Array<number | null>;
  laagste: Array<number | null>;
}): React.JSX.Element {
  const alle = [...eigen, ...laagste].filter((v): v is number => v !== null);
  const dagen = Math.max(eigen.length, laagste.length);

  if (alle.length === 0 || dagen === 0) {
    return <svg viewBox={`0 0 ${BREEDTE} ${HOOGTE}`} style={{ width: '100%', display: 'block' }} />;
  }

  let onder = Math.min(...alle);
  let boven = Math.max(...alle);
  if (onder === boven) {
    // Eén prijsniveau: een vaste marge, anders wordt de lijn door nul gedeeld weggeschaald.
    onder -= 50;
    boven += 50;
  }

  const x = (index: number): number =>
    RAND + (dagen < 2 ? BREEDTE - 2 * RAND : (index * (BREEDTE - 2 * RAND)) / (dagen - 1));
  const y = (waarde: number): number =>
    RAND + ((boven - waarde) / (boven - onder)) * (HOOGTE - 2 * RAND);

  /** Zelfde regel als het grote diagram: een lege meting onderbreekt de lijn. */
  function pad(waarden: Array<number | null>): string {
    let d = '';
    let open = false;
    waarden.forEach((waarde, index) => {
      if (waarde === null) {
        open = false;
        return;
      }
      d += `${open ? 'L' : 'M'}${x(index).toFixed(1)},${y(waarde).toFixed(1)} `;
      open = true;
    });
    return d.trim();
  }

  return (
    <svg
      viewBox={`0 0 ${BREEDTE} ${HOOGTE}`}
      style={{ width: '100%', height: 'auto', display: 'block' }}
      aria-hidden="true"
    >
      <path
        d={pad(eigen)}
        fill="none"
        stroke="var(--c-line-own)"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d={pad(laagste)}
        fill="none"
        stroke="var(--c-line-3)"
        strokeWidth="2.2"
        strokeLinejoin="round"
      />
    </svg>
  );
}
